export const AUDIO_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

export const BROWSER_DEFAULT_OUTPUT_SINK_ID = 'default';

export function getBrowserDefaultCaptureConstraints(): MediaTrackConstraints {
  return { ...AUDIO_CAPTURE_CONSTRAINTS };
}

export async function acquireBrowserDefaultMicStream(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: getBrowserDefaultCaptureConstraints(),
    video: false,
  });
}

type MediaElementWithSinkId = HTMLMediaElement & {
  setSinkId?: (sinkId: string) => Promise<void>;
};

export async function applyBrowserDefaultAudioOutput(audioEl: HTMLMediaElement | null): Promise<boolean> {
  if (!audioEl) return false;
  const el = audioEl as MediaElementWithSinkId;
  if (typeof el.setSinkId !== 'function') {
    return true;
  }
  try {
    await el.setSinkId(BROWSER_DEFAULT_OUTPUT_SINK_ID);
    return true;
  } catch {
    try {
      await el.setSinkId('');
      return true;
    } catch {
      return false;
    }
  }
}

export async function listAudioOutputDeviceIds(): Promise<string[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === 'audiooutput' && d.deviceId).map((d) => d.deviceId);
}

export async function listAudioInputDeviceIds(): Promise<string[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((d) => d.kind === 'audioinput' && d.deviceId).map((d) => d.deviceId);
}

export function createAudioOutputDeviceTracker() {
  let knownOutputIds: string[] = [];

  const refreshKnownOutputs = async (): Promise<string[]> => {
    const ids = await listAudioOutputDeviceIds();
    knownOutputIds = ids;
    return ids;
  };

  const snapshotOutputs = (): void => {
    void refreshKnownOutputs();
  };

  const detectOutputDeviceChange = async (): Promise<{ added: boolean; removed: boolean }> => {
    const prev = knownOutputIds;
    const next = await listAudioOutputDeviceIds();
    const prevSet = new Set(prev);
    const nextSet = new Set(next);
    knownOutputIds = next;

    const added =
      next.length > 0 && (prev.length === 0 || next.length > prev.length || next.some((id) => !prevSet.has(id)));
    const removed =
      prev.length > 0 &&
      (next.length === 0 || next.length < prev.length || prev.some((id) => !nextSet.has(id)));

    return { added, removed };
  };

  return { snapshotOutputs, detectOutputDeviceChange, refreshKnownOutputs };
}

export function createAudioInputDeviceTracker() {
  let knownInputIds: string[] = [];

  const refreshKnownInputs = async (): Promise<string[]> => {
    const ids = await listAudioInputDeviceIds();
    knownInputIds = ids;
    return ids;
  };

  const snapshotInputs = (): void => {
    void refreshKnownInputs();
  };

  const detectInputDeviceChange = async (): Promise<{ added: boolean; removed: boolean }> => {
    const prev = knownInputIds;
    const next = await listAudioInputDeviceIds();
    const prevSet = new Set(prev);
    const nextSet = new Set(next);
    knownInputIds = next;

    const added =
      next.length > 0 && (prev.length === 0 || next.length > prev.length || next.some((id) => !prevSet.has(id)));
    const removed =
      prev.length > 0 &&
      (next.length === 0 || next.length < prev.length || prev.some((id) => !nextSet.has(id)));

    return { added, removed };
  };

  return { snapshotInputs, detectInputDeviceChange, refreshKnownInputs };
}
